import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../utils/api";
import { diasDeCalendario } from "./diasDeCalendario";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleSlash,
  Clock,
  Inbox,
  Loader2,
  XCircle,
  Zap,
} from "lucide-react";
import { analisarAvaliacao } from "./portalEvaluation";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { formatApiError } from "../../utils/api";
import { prazoDoAcionamento } from "../../utils/portalPrazo";
import ServiceTypeSelect, { tiposComoOpcoes } from "../../components/ServiceTypeSelect";
import ServiceOSLink from "./ServiceOSLink";
import { subscribeAdminWS } from "../../lib/adminWS";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const PAGINA = 20;

const FILTROS = [
  { id: "", label: "Todos" },
  { id: "pending", label: "Pendentes" },
  { id: "accepted", label: "Aceitos" },
  { id: "rejected", label: "Recusados" },
  { id: "ignored", label: "Ignorados" },
  { id: "expired", label: "Expirados" },
  { id: "needs_service_type", label: "Aguardando tipo" },
];

const STATUS_META = {
  pending: { rotulo: "Pendente", icone: Clock, classe: "bg-amber-50 text-amber-700" },
  accepted: { rotulo: "Aceito", icone: CheckCircle2, classe: "bg-emerald-50 text-emerald-700" },
  rejected: { rotulo: "Recusado", icone: XCircle, classe: "bg-red-50 text-red-700" },
  ignored: { rotulo: "Ignorado", icone: CircleSlash, classe: "bg-gray-100 text-gray-600" },
  expired: { rotulo: "Expirado", icone: CircleSlash, classe: "bg-gray-100 text-gray-500" },
};

const desde = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return "";
  const min = Math.floor(ms / 60000);
  const d = diasDeCalendario(iso);
  if (d >= 1) return d < 30 ? `${d}d atrás` : new Date(iso).toLocaleDateString("pt-BR");
  if (min < 1) return "agora";
  if (min < 60) return `${min}m atrás`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m atrás`;
};

const ConjuntoFalho = ({ item }) => {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left"
      >
        <ChevronRight
          size={13}
          className={`shrink-0 text-gray-400 transition-transform ${aberto ? "rotate-90" : ""}`}
        />
        <span className="text-[12px] font-medium text-gray-800 flex-1 truncate">{item.nome}</span>
        <span className="shrink-0 text-[10px] font-bold text-gray-500 bg-white border border-gray-200 rounded-full px-1.5 py-0.5">
          {item.falhas.length} {item.falhas.length === 1 ? "condição" : "condições"}
        </span>
      </button>
      {aberto && (
        <div className="px-2.5 pb-2 space-y-1.5">
          {item.falhas.map((f, i) => (
            <div key={i} className="flex items-start gap-1.5" title={f.bruto}>
              <XCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-600 leading-snug">
                <span className="font-semibold text-gray-800">{f.rotulo}</span>
                {f.detalhe ? ` — ${f.detalhe}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Avaliacao = ({ det }) => {
  const a = analisarAvaliacao(det);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold decoration-dotted underline-offset-4 underline"
          style={{ color: a.casou ? "#047857" : "#B45309" }}
          data-testid={`detection-eval-${det.id}`}
        >
          {a.casou ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
          {a.casou ? "Casou" : "Não casou"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[420px] p-3 space-y-2 z-[1750]"
        data-testid={`detection-eval-detail-${det.id}`}
      >
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
          Avaliação das regras
        </p>
        {a.casou ? (
          <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 size={15} />
            Correspondeu ao conjunto {a.conjunto || "—"}
          </p>
        ) : (
          <>
            <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
              <AlertTriangle size={15} />
              Nenhum conjunto correspondeu
              <span className="text-[11px] font-normal text-gray-400">
                ({a.avaliados} avaliado{a.avaliados === 1 ? "" : "s"})
              </span>
            </p>
            {a.motivos.map((m, i) => (
              <div key={i} className="rounded-lg bg-orange-50/70 border border-orange-100 px-2.5 py-2" title={m.bruto} data-testid={`detection-eval-motivo-${det.id}-${i}`}>
                <p className="text-[12px] text-gray-700">
                  <span className="font-semibold">Motivo:</span> {m.rotulo}
                  {m.detalhe ? ` — ${m.detalhe}` : ""}
                </p>
              </div>
            ))}
            {a.maisProximo && (
              <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-2.5 py-2">
                <p className="text-[12px] text-gray-700">
                  <span className="font-semibold">Mais perto:</span> {a.maisProximo.nome} — faltou{" "}
                  <span className="font-semibold">
                    {[...new Set(a.maisProximo.falhas.map((f) => f.rotulo))].join(", ")}
                  </span>
                </p>
              </div>
            )}
            {a.conjuntos.map((c) => (
              <ConjuntoFalho key={c.id} item={c} />
            ))}
            {!a.conjuntos.length && !a.motivos.length && (
              <p className="text-[12px] text-gray-500">
                {a.cru
                  ? "O portal não detalhou as condições. Conteúdo recebido:"
                  : "O portal não informou o motivo desta avaliação."}
              </p>
            )}
            {!a.conjuntos.length && a.cru && (
              <pre className="text-[10px] bg-gray-50 border border-gray-100 rounded-lg p-2 overflow-x-auto text-gray-600">
                {JSON.stringify(a.cru, null, 2)}
              </pre>
            )}
            {det.rejection_notes && (
              <p className="text-[11px] text-gray-500">Observação: {det.rejection_notes}</p>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Acionamento aceito sem de-para: escolher o tipo aqui abre a OS que faltou. */
const DefinirTipo = ({ det, onPronto }) => {
  const [aberto, setAberto] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [tipo, setTipo] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto || tipos.length) return;
    api
      .get("/service-types")
      .then(({ data }) => setTipos((data || []).filter((t) => t.active !== false)))
      .catch(() => setTipos([]));
  }, [aberto, tipos.length]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const { data } = await api.post(`/portals/detections/${det.id}/service-type`, {
        service_type_id: tipo,
      });
      toast.success(
        data?.sugestao_registrada
          ? "OS criada. Sua escolha foi enviada como sugestão de de-para."
          : "OS criada e aguardando despacho.",
      );
      setAberto(false);
      onPronto?.();
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível criar a OS."));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 hover:bg-amber-100"
          data-testid={`detection-set-type-${det.id}`}
        >
          <AlertTriangle size={10} /> Definir tipo de serviço
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-3 space-y-2 z-[1750]">
        <p className="text-[11px] text-gray-500 leading-snug">
          Sem de-para para <strong>{det.service_type || "este tipo"}</strong>. Escolha o tipo de
          serviço do AutEM para abrir a OS — a escolha vira sugestão de de-para.
        </p>
        <ServiceTypeSelect
          options={tiposComoOpcoes(tipos)}
          value={tipo}
          onChange={(folha) => setTipo(folha?.id || "")}
          placeholder="Tipo de serviço"
          emptyText="Nenhum tipo cadastrado"
          testid={`detection-type-select-${det.id}`}
        />
        <Button
          className="h-8 w-full text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          disabled={!tipo || salvando}
          onClick={salvar}
          data-testid={`detection-type-confirm-${det.id}`}
        >
          {salvando ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Criar OS
        </Button>
      </PopoverContent>
    </Popover>
  );
};

/** Por que a OS não foi cadastrada — sem isto o não-cadastro era invisível. */
const MotivoOS = ({ det }) => {
  const motivo = det.os_motivo || null;
  if (!motivo?.texto) return null;
  const rotulo =
    { sem_destino: "Sem filial na integração", sem_de_para: "Sem de-para", erro: "Erro ao cadastrar" }[
      motivo.codigo
    ] || "OS não cadastrada";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 hover:bg-red-100"
          data-testid={`detection-os-reason-${det.id}`}
        >
          <AlertTriangle size={10} /> {rotulo}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[340px] p-3 space-y-1.5 z-[1750]"
        data-testid={`detection-os-reason-detail-${det.id}`}
      >
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
          OS não cadastrada
        </p>
        <p className="text-[12px] text-gray-700 leading-snug">{motivo.texto}</p>
        {motivo.em && <p className="text-[10px] text-gray-400">Em {quando(motivo.em)}</p>}
      </PopoverContent>
    </Popover>
  );
};

// Data e hora completas (com segundos) para auditoria de detecção e aceite.
const quandoCompleto = (iso) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "—";

const AuditoriaDatas = ({ det }) => {
  const [aberto, setAberto] = useState(false);
  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => setAberto(true)}
          onMouseLeave={() => setAberto(false)}
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Ver data e hora da detecção e do aceite"
          data-testid={`portal-auditoria-datas-btn-${det.id}`}
        >
          <Clock size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="w-auto p-3 text-[12px]"
        onMouseEnter={() => setAberto(true)}
        onMouseLeave={() => setAberto(false)}
        data-testid={`portal-auditoria-datas-${det.id}`}
      >
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-1 tabular-nums">
          <span className="text-gray-400">Detecção</span>
          <span className="text-gray-800 font-medium" data-testid={`portal-historico-detectado-${det.id}`}>
            {quandoCompleto(det.first_detected_at || det.detected_at)}
          </span>
          <span className="text-gray-400">Aceite</span>
          <span
            className={det.accepted_at ? "text-emerald-700 font-medium" : "text-gray-400"}
            data-testid={`portal-historico-aceito-${det.id}`}
          >
            {det.accepted_at ? quandoCompleto(det.accepted_at) : "Sem aceite"}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const quando = (iso) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Histórico de acionamentos, com rolagem infinita.
 *  `variant="page"` desenha a tabela larga da página de Acionamentos;
 *  o padrão é a lista compacta usada dentro do drawer da automação. */
export default function PortalDetectionsTab({ workerId, variant = "drawer", nomePorWorker, days = 0, periodo }) {
  const [filtro, setFiltro] = useState("");
  const [itens, setItens] = useState([]);
  const [stats, setStats] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [fim, setFim] = useState(false);
  const sentinela = useRef(null);
  const buscando = useRef(false);

  const carregar = useCallback(
    async (offset, status) => {
      if (buscando.current) return;
      buscando.current = true;
      if (offset === 0) setCarregando(true);
      try {
        const { data } = await api.get("/portals/detections", {
          params: { worker_id: workerId, status, days, ...(periodo || {}), limit: PAGINA, offset },
        });
        const lote = data?.items || [];
        setItens((l) => (offset === 0 ? lote : [...l, ...lote]));
        if (data?.stats) setStats(data.stats);
        setFim(lote.length < PAGINA);
      } finally {
        buscando.current = false;
        setCarregando(false);
      }
    },
    [workerId, days, periodo],
  );

  useEffect(() => {
    setItens([]);
    setFim(false);
    carregar(0, filtro);
  }, [filtro, carregar]);

  // Aceite (nesta tela, por um colega ou pelo webhook da automação) atualiza a
  // linha na hora: o payload do aceite traz o protocolo revelado pelo portal,
  // o endereço corrigido e o link da OS, e antes disso só aparecia ao recarregar.
  useEffect(() => {
    const aplicar = (detection, novo) => {
      if (!detection?.id || detection.teste) return;
      if (workerId && detection.worker_id !== workerId) return;
      setItens((l) => {
        const i = l.findIndex((d) => d.id === detection.id);
        if (i >= 0) {
          const proximo = [...l];
          proximo[i] = { ...proximo[i], ...detection };
          return proximo;
        }
        // Chamado que ainda não está na página carregada: só entra no topo se
        // couber no filtro atual (senão a lista mentiria sobre o filtro).
        if (!novo || (filtro && filtro !== (detection.status || "pending").toLowerCase())) return l;
        return [detection, ...l];
      });
    };
    const off1 = subscribeAdminWS("portal.detection", ({ detection }) => aplicar(detection, true));
    const off2 = subscribeAdminWS("portal.detection.updated", ({ detection }) =>
      aplicar(detection, false),
    );
    return () => {
      off1?.();
      off2?.();
    };
  }, [workerId, filtro]);


  // Lazy loading: pede a página seguinte quando a sentinela entra na tela.
  useEffect(() => {
    const alvo = sentinela.current;
    if (!alvo || fim) return undefined;
    const obs = new IntersectionObserver(
      (entradas) => {
        if (entradas[0].isIntersecting && !buscando.current) carregar(itens.length, filtro);
      },
      { rootMargin: "120px" },
    );
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [itens.length, fim, filtro, carregar]);

  return (
    <div className="space-y-3" data-testid="portal-detections-tab">
      {!!stats?.needs_service_type && filtro !== "needs_service_type" && (
        <button
          type="button"
          onClick={() => setFiltro("needs_service_type")}
          className="w-full flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-colors hover:bg-amber-100"
          data-testid="detections-needs-type-banner"
        >
          <AlertTriangle size={14} className="shrink-0 text-amber-600" />
          <span className="flex-1 text-[12px] font-semibold text-amber-800">
            {stats.needs_service_type} acionamento
            {stats.needs_service_type > 1 ? "s aceitos aguardando" : " aceito aguardando"} tipo de
            serviço — a OS só é aberta depois da escolha.
          </span>
          <span className="shrink-0 text-[11px] font-bold text-amber-700 underline">Resolver</span>
        </button>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTROS.map((f) => {
          const ativo = filtro === f.id;
          const n = f.id ? stats?.[f.id] : stats?.detected;
          return (
            <button
              key={f.id || "todos"}
              type="button"
              onClick={() => setFiltro(f.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                ativo
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`detections-filter-${f.id || "todos"}`}
            >
              {f.label}
              {n != null && <span className="text-[10px] opacity-70">{n}</span>}
            </button>
          );
        })}
      </div>

      {carregando && !itens.length ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gray-300" size={20} />
        </div>
      ) : !itens.length ? (
        <div className="text-center py-14 text-gray-400" data-testid="detections-empty">
          <Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-semibold">Nenhum acionamento por aqui</p>
          <p className="text-xs mt-1">
            O que o portal detectar aparece nesta lista com o resultado da avaliação.
          </p>
        </div>
      ) : variant === "page" ? (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <Table className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
            <TableHeader>
              <TableRow className="bg-gray-50/80">
                <TableHead>Quando</TableHead>
                <TableHead>Protocolo</TableHead>
                {nomePorWorker && <TableHead>Integração</TableHead>}
                <TableHead>Contratante</TableHead>
                <TableHead>Serviço</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead>Avaliação</TableHead>
                <TableHead>Situação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map((d) => {
                const st = STATUS_META[(d.status || "pending").toLowerCase()] || STATUS_META.pending;
                const Icone = st.icone;
                return (
                  <TableRow key={d.id} className="text-sm" data-testid={`portal-detection-${d.id}`}>
                    <TableCell className="text-gray-500 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {quando(d.detected_at)}
                        <AuditoriaDatas det={d} />
                      </span>
                      <span className="block text-[10px] text-gray-400">{desde(d.detected_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-gray-900 tabular-nums">
                        {d.protocol || d.external_service_id || "—"}
                      </span>
                      <span
                        className={`block w-fit mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          d.attendance_type === "SCHEDULED"
                            ? "bg-sky-50 text-sky-700"
                            : "bg-orange-50 text-orange-700"
                        }`}
                      >
                        {d.attendance_type === "SCHEDULED" ? "Agendado" : "Emergencial"}
                      </span>
                      {/* Quando tem que acontecer: data do agendamento ou a
                          previsão do contratante nos emergenciais. */}
                      {prazoDoAcionamento(d) && (
                        <span
                          className="block text-[10px] text-gray-500 mt-0.5"
                          data-testid={`portal-historico-prazo-${d.id}`}
                        >
                          {prazoDoAcionamento(d)}
                        </span>
                      )}
                    </TableCell>
                    {nomePorWorker && (
                      <TableCell className="text-gray-600">
                        {nomePorWorker[d.worker_id] || "—"}
                      </TableCell>
                    )}
                    <TableCell className="font-semibold text-gray-900">
                      {d.contractor_name || "—"}
                      {d.customer_name && (
                        <span className="block text-[11px] font-normal text-gray-500">
                          {d.customer_name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">{d.service_type || "—"}</TableCell>
                    <TableCell className="text-gray-600">
                      {[d.origin?.bairro, d.origin?.cidade, d.origin?.uf]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {d.vehicle?.placa || d.vehicle?.modelo || "—"}
                    </TableCell>
                    <TableCell>
                      <Avaliacao det={d} />
                      {d.matched_rule_set_name && (
                        <span className="block text-[10px] text-gray-400">
                          {d.matched_rule_set_name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md ${st.classe}`}
                      >
                        <Icone size={11} /> {st.rotulo}
                      </span>
                      {d.service_id && (
                        <span className="block">
                          <ServiceOSLink
                            serviceId={d.service_id}
                            protocolo={d.service_protocolo}
                            cancelado={d.service_cancelado}
                            testId={`detection-os-link-${d.id}`}
                          />
                        </span>
                      )}
                      {d.needs_service_type && !d.service_id && (
                        <DefinirTipo det={d} onPronto={() => carregar(0, filtro)} />
                      )}
                      {!d.service_id && !d.needs_service_type && <MotivoOS det={d} />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!fim && (
            <div ref={sentinela} className="flex items-center justify-center py-4">
              <Loader2 className="animate-spin text-gray-300" size={16} />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          {itens.map((d) => {
            const st = STATUS_META[(d.status || "pending").toLowerCase()] || STATUS_META.pending;
            const Icone = st.icone;
            return (
              <div
                key={d.id}
                className="px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50/70"
                data-testid={`detection-row-${d.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold text-gray-900 tabular-nums shrink-0">
                    {d.protocol || d.external_service_id || "—"}
                  </span>
                  <span
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      d.attendance_type === "SCHEDULED"
                        ? "bg-sky-50 text-sky-700"
                        : "bg-orange-50 text-orange-700"
                    }`}
                  >
                    <Zap size={9} />
                    {d.attendance_type === "SCHEDULED" ? "Agendado" : "Emergencial"}
                  </span>
                  {prazoDoAcionamento(d) && (
                    <span
                      className="shrink-0 text-[10px] text-gray-500"
                      data-testid={`portal-historico-prazo-${d.id}`}
                    >
                      {prazoDoAcionamento(d)}
                    </span>
                  )}
                  <span className="flex-1" />
                  <span
                    className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md ${st.classe}`}
                  >
                    <Icone size={11} /> {st.rotulo}
                  </span>
                  <AuditoriaDatas det={d} />
                  <span className="shrink-0 text-[11px] text-gray-400 tabular-nums">
                    {desde(d.detected_at)}
                  </span>
                </div>

                <p className="text-[12px] text-gray-700 mt-1.5 truncate">
                  <span className="font-semibold text-gray-800">{d.contractor_name || "—"}</span>
                  {d.customer_name ? ` · ${d.customer_name}` : ""}
                </p>
                <p className="text-[11px] text-gray-500 truncate mt-0.5">
                  {d.service_type || "—"}
                </p>
                {d.needs_service_type && !d.service_id && (
                  <DefinirTipo det={d} onPronto={() => carregar(0, filtro)} />
                )}
                {!d.service_id && !d.needs_service_type && <MotivoOS det={d} />}

                <div className="flex items-center gap-2 mt-1.5">
                  <p className="flex-1 min-w-0 text-[11px] text-gray-400 truncate">
                    {[d.origin?.bairro, d.origin?.cidade, d.origin?.uf].filter(Boolean).join(", ") ||
                      "origem não informada"}
                  </p>
                  <div className="shrink-0 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Avaliacao det={d} />
                      {d.service_id && (
                        <ServiceOSLink
                          serviceId={d.service_id}
                          protocolo={d.service_protocolo}
                          cancelado={d.service_cancelado}
                          testId={`detection-os-link-${d.id}`}
                          compacto
                        />
                      )}
                    </div>
                    {d.matched_rule_set_name && (
                      <p className="text-[10px] text-gray-400 truncate">
                        {d.matched_rule_set_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {!fim && (
            <div ref={sentinela} className="flex items-center justify-center py-4">
              <Loader2 className="animate-spin text-gray-300" size={16} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
